import { createGlobalStyle } from 'styled-components';
import {
  colorVars,
  fontVars,
  layoutVars,
  motionVars,
  nightNavVars,
  typeVars,
  typeVarsMobile,
} from './tokens';

/* Prints a token map as CSS declarations, so tokens.ts and :root can never drift. */
const declare = (vars: Record<string, string>): string =>
  Object.entries(vars)
    .map(([name, value]) => `${name}: ${value};`)
    .join('\n    ');

const GlobalStyles = createGlobalStyle`
  :root {
    /* color (c.1 to c.5) */
    ${declare(colorVars)}
    /* type (c.6) */
    ${declare(fontVars)}
    ${declare(typeVars)}
    /* motion (e.1, e.2) */
    ${declare(motionVars)}
    /* spacing, containers, radius (c.7) */
    ${declare(layoutVars)}
    --progress-top: 0px;
  }

  /* The active-section observer stamps the band of the section in view on <html>.
     TopBar, SectionRail and AskDock read only the --nav-* vars, so one component serves both grounds. */
  html[data-band='night'] {
    ${declare(nightNavVars)}
  }

  html[data-nav='on'] {
    --progress-top: calc(var(--nav-h) - 2px);
  }

  @media (max-width: 640px) {
    :root {
      ${declare(typeVarsMobile)}
      --nav-h: 44px;
      --section-pad: var(--section-pad-mobile);
    }
  }

  @media (max-width: 400px) {
    :root {
      --gutter: 20px;
    }
  }

  html {
    scroll-behavior: smooth;
    background-color: var(--bg);
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    color: var(--ink-dim);
    background-color: var(--bg);
    font-family: var(--font-body);
    text-align: left;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  h1, h2, h3, h4, h5, h6 {
    font-weight: 500;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  button {
    font: inherit;
    color: inherit;
    background: none;
    border: 0;
    cursor: pointer;
  }

  /* Every mono element and every counting element uses tabular figures,
     so a rAF count-up never reflows its container. */
  code, kbd, samp, pre, [data-mono], [data-count] {
    font-variant-numeric: tabular-nums;
  }

  /* All sections carry the same scroll margin under the fixed bar. */
  section[id], [data-band][id] {
    scroll-margin-top: var(--scroll-margin);
  }

  ::selection {
    background: var(--selection-bg);
    color: var(--selection-ink);
  }

  a:focus-visible,
  button:focus-visible,
  textarea:focus-visible,
  input:focus-visible,
  select:focus-visible,
  [tabindex]:focus-visible {
    outline: 2px solid var(--focus-ring);
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }
`;

export default GlobalStyles;
